// FILE: ExamFormManagement.tsx
import { useSessionClass } from 'contexts/SessionClassContext'
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TenthExamFromTemplate from '../../../components/templates/TenthExamFromTemplate'
import NinthExamFormTemplate from 'components/templates/NinthExamFormTemplate'
import EleventhExamFormTemplate from 'components/templates/EleventhExamFormTemplate'
import TwelfthExamFormTemplate from 'components/templates/TwelfthExamFormTemplate'
import GetExamFormModal from '../../../components/Modals/GetExamFormModal'
import {
  Alert, Box, Typography, CircularProgress, Button, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, FormControl, InputLabel, Select,
  MenuItem, LinearProgress, Checkbox, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material'
import { LoadingButton } from "@mui/lab";
import CloseIcon from '@mui/icons-material/Close'
import { Student, useStudents } from '../../../hooks/useStudents'
import { useClasses } from '../../../hooks/useClasses'
import { useSessions } from '../../../hooks/useSession'
import { useAuth } from '../../../contexts/AuthContext'
import { AuthProvider } from '../../../contexts/AuthContext'
import { useSchoolIdForAdmin } from '../../../hooks/useSchoolIdForAdmin'
import { useError } from '../../../contexts/ErrorContext'
import { getStudentData } from '../../../utils/storage'
import { useCurrentStudent } from '../../../hooks/useCurrentStudent'
import jsPDF from "jspdf"
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { theme } from '../../../theme'
import html2canvas from "html2canvas"
// @ts-ignore
import html2pdf from "html2pdf.js"
import { ClassNames } from '@emotion/react'

// QueryClient for bulk generation
const bulkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Tiny helpers to keep the UI responsive during long loops
const waitNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

const yieldToBrowser = (forceDelay = false) =>
  new Promise<void>((resolve) => {
    if (forceDelay) {
      setTimeout(resolve, 50); // Reduced from 120ms
    } else if ('requestIdleCallback' in window) {
      // @ts-ignore
      requestIdleCallback(() => resolve(), { timeout: 50 }); // Reduced from 100ms
    } else {
      setTimeout(resolve, 10); // Reduced from 40ms
    }
  });

/** ---------------- Font readiness helper ---------------- */
async function waitForFonts() {
  try {
    // @ts-ignore
    if (document.fonts?.ready) await (document as any).fonts.ready;
  } catch {}
}

/** ---------- Helper functions for html2pdf.js approach ---------- */

/** ---------- Types ---------- */
interface StudentData {
  id: number;
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  studentCode: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[];
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparId: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  section: string;
  subjects: string | string[];
  gmail: string;
  admissionDate: string | number[];
  admissionClass: string;
  schoolId: number;
  schoolClassId: number;
  sessionName: string;
  sessionId: number;
  className: string;
  userId?: number;
  status: boolean;
  studentPhoto?: string | File;
  /** Bulk PDF: pre-fetched blob URLs (object URLs) — avoids per-student /studentPhoto API */
  photoUrl?: string | null;
  signatureUrl?: string | null;
  /** When true, templates skip useStudentPhoto / useStudentSignature (bulk already resolved photo info) */
  skipStudentMediaFetch?: boolean;
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field
}

const detectClassFlags = (className?: string | null) => {
  const normalized = (className || '').toLowerCase();
  const numbers = (normalized.match(/\d+/g) || []).map((num) => parseInt(num, 10));
  const hasNumber = (n: number) => numbers.includes(n);
  const isNinthClass =
    hasNumber(9) ||
    normalized.includes('class 9') ||
    normalized.includes('9th') ||
    normalized.includes('ninth');
  const isTenthClass =
    hasNumber(10) ||
    normalized.includes('class 10') ||
    normalized.includes('10th') ||
    normalized.includes('tenth');
  const isEleventhClass =
    hasNumber(11) ||
    normalized.includes('class 11') ||
    normalized.includes('11th') ||
    normalized.includes('eleventh');
  const isTwelfthClass =
    hasNumber(12) ||
    normalized.includes('class 12') ||
    normalized.includes('12th') ||
    normalized.includes('twelfth');
  return { isNinthClass, isTenthClass, isEleventhClass, isTwelfthClass };
};

// Helper function to detect if class is 10th, 11th or 12th (for Exam Form vs Enrollment Form)
const isExamFormClass = (className?: string | null) => {
  if (!className) return false;
  const normalized = className.toLowerCase();
  const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
  const hasNumber = (n: number) => numbers.has(n);
  return (
    hasNumber(10) ||
    hasNumber(11) ||
    hasNumber(12) ||
    normalized.includes('class 10') ||
    normalized.includes('class 11') ||
    normalized.includes('class 12') ||
    normalized.includes('10th') ||
    normalized.includes('11th') ||
    normalized.includes('12th') ||
    normalized.includes('tenth') ||
    normalized.includes('eleventh') ||
    normalized.includes('twelfth')
  );
};

const ExamFormManagement = () => {
  const { t } = useTranslation();
  const { selectedSession, selectedClass, selectedClassName } = useSessionClass();
  
  // Helper function to get the appropriate form text based on selected class
  const getFormText = (examFormKey: string, enrollmentFormKey: string) => {
    const className = selectedClass?.name || selectedClassName || '';
    return isExamFormClass(className) ? t(examFormKey) : t(enrollmentFormKey);
  };

  // Helper function to get the appropriate subtitle text based on selected class
  const getSubtitleText = (examFormKey: string, enrollmentFormKey: string) => {
    const className = selectedClass?.name || selectedClassName || '';
    const { isNinthClass } = detectClassFlags(className);
    const supported = isSupportedClass();
    
    if (!supported) {
      // Show "No ExamForm/Enrollment form Issued" message for unsupported classes
      return isNinthClass 
        ? t('no_enrollment_form_issued_for_class', { className })
        : t('no_exam_form_issued_for_class', { className });
    }
    
    return isNinthClass ? t(enrollmentFormKey) : t(examFormKey);
  };

  // Helper function to check if the selected class is 9th, 10th, 11th or 12th
  const isSupportedClass = () => {
    const className = selectedClass?.name || selectedClassName || '';
    const { isNinthClass, isTenthClass, isEleventhClass, isTwelfthClass } = detectClassFlags(className);
    return isNinthClass || isTenthClass || isEleventhClass || isTwelfthClass;
  };
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
  const [showExamForm, setShowExamForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Bulk download state
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSelectedClass, setBulkSelectedClass] = useState<number | string | ''>('');
  const [bulkSelectedSession, setBulkSelectedSession] = useState<number | ''>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [currentStudent, setCurrentStudent] = useState('');
  const isBulkDownloadCancelledRef = useRef(false);
  const [bulkSelectedStudentIds, setBulkSelectedStudentIds] = useState<Set<number>>(new Set());

  // Auth and data hooks
  const { user, isSchoolAdmin } = useAuth();
  const { showSuccess, showError } = useError();
  const isStudent = user?.roles?.includes('STUDENT');
  // Use user.schoolId directly for all roles to ensure correct school ID
  const effectiveSchoolId = user?.schoolId ?? undefined;

  // For students, fetch current student data directly
  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();

  const { studentsQuery, useStudentsBySessionIdAndClassId, useStudentsBySessionIdAndClassName, getStudentByStudentCode, getStudentPhotoInfosBulk } =
    useStudents(false, effectiveSchoolId);
  const { classesQuery } = useClasses(effectiveSchoolId);
  const { sessionsQuery } = useSessions(effectiveSchoolId);

  const classes = classesQuery.data || [];
  const sessions = sessionsQuery.data || [];
  const classesLoading = classesQuery.isLoading;
  const classesError = classesQuery.error;
  const allBulkStudents = studentsQuery.data || [];

  // Normalize bulk selected session and class IDs
  const normalizedBulkSession =
    typeof bulkSelectedSession === "number" && !Number.isNaN(bulkSelectedSession)
      ? bulkSelectedSession
      : undefined;

  const normalizedBulkClassId =
    typeof bulkSelectedClass === "number" && !Number.isNaN(bulkSelectedClass) ? bulkSelectedClass : undefined;

  const normalizedBulkClassName =
    typeof bulkSelectedClass === "string" && bulkSelectedClass.trim().length > 0 ? bulkSelectedClass : undefined;

  // Use filtered API query when both session and class are selected
  const studentsByIdQuery = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : normalizedBulkSession,
    isStudent ? undefined : normalizedBulkClassId
  );
  const studentsByNameQuery = useStudentsBySessionIdAndClassName(
    isStudent ? undefined : normalizedBulkSession,
    isStudent ? undefined : normalizedBulkClassName
  );
  const activeBulkStudentsQuery = normalizedBulkClassName ? studentsByNameQuery : studentsByIdQuery;
  const { data: filteredBulkStudents = [], isLoading: isLoadingFilteredBulkStudents } = activeBulkStudentsQuery;

  // Use filtered students from API when both session and class are selected, otherwise filter all students
  const bulkStudents = useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (normalizedBulkSession && (normalizedBulkClassId || normalizedBulkClassName)) {
      return filteredBulkStudents || [];
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!allBulkStudents) return [];

    let filtered = allBulkStudents;

    // Filter by session if selected
    if (bulkSelectedSession && typeof bulkSelectedSession === 'number') {
      filtered = filtered.filter(
        (student: any) => Number(student.sessionId) === bulkSelectedSession
      );
    }

    // Filter by class if selected
    if (bulkSelectedClass && typeof bulkSelectedClass === 'number') {
      filtered = filtered.filter(
        (student: any) => Number(student.schoolClassId) === bulkSelectedClass
      );
    }

    return filtered;
  }, [
    normalizedBulkSession, 
    normalizedBulkClassId,
    normalizedBulkClassName,
    filteredBulkStudents, 
    allBulkStudents, 
    bulkSelectedSession, 
    bulkSelectedClass
  ]);

  const bulkVisibleActiveStudents = useMemo(() => {
    return bulkStudents
      .filter((s: any) => s.status === true && typeof s.id === "number")
      .slice()
      .sort((a: any, b: any) =>
        String(a.studentName || "").localeCompare(String(b.studentName || ""))
      );
  }, [bulkStudents]);

  useEffect(() => {
    setBulkSelectedStudentIds(
      new Set(bulkVisibleActiveStudents.map((s: any) => s.id as number))
    );
  }, [
    normalizedBulkSession,
    normalizedBulkClassId,
    normalizedBulkClassName,
    bulkVisibleActiveStudents,
  ]);

  const bulkSelectAllChecked =
    bulkVisibleActiveStudents.length > 0 &&
    bulkVisibleActiveStudents.every((s: any) => bulkSelectedStudentIds.has(s.id));
  const bulkSelectAllIndeterminate =
    bulkSelectedStudentIds.size > 0 && !bulkSelectAllChecked;

  // Convert Student to StudentData the way template expects
  const convertStudentToStudentData = (student: Student): StudentData => {
    return {
      id: student.id,
      applicationNo: student.applicationNo,
      rollNumber: student.rollNumber,
      enrolmentNo: student.enrolmentNo,
      studentCode: student.studentCode,
      studentName: student.studentName,
      fatherName: student.fatherName,
      motherName: student.motherName,
      dateOfBirth: Array.isArray(student.dateOfBirth)
        ? `${student.dateOfBirth[0]}-${student.dateOfBirth[1].toString().padStart(2, '0')}-${student.dateOfBirth[2].toString().padStart(2, '0')}`
        : student.dateOfBirth,
      gender: student.gender,
      medium: student.medium,
      caste: student.caste,
      subCaste: student.subCaste,
      casteCertificate: student.casteCertificate,
      religion: student.religion,
      sssmid: student.sssmid,
      scholarNo: student.scholarNo,
      penNo: student.penNo,
      apparId: student.apparId,
      aadharNo: student.aadharNo,
      houseNo: student.houseNo,
      street: student.street,
      landmark: student.landmark,
      area: student.area,
      city: student.city,
      postOffice: student.postOffice,
      district: student.district,
      state: student.state,
      pinCode: student.pinCode,
      mobileNo: student.mobileNo,
      bankName: student.bankName,
      ifcCode: student.ifcCode,
      accountNo: student.accountNo,
      annualIncome: student.annualIncome,
      section: student.section,
      subjects: student.subjects,
      gmail: student.gmail,
      admissionDate: Array.isArray(student.admissionDate)
        ? `${student.admissionDate[0]}-${student.admissionDate[1].toString().padStart(2, '0')}-${student.admissionDate[2].toString().padStart(2, '0')}`
        : student.admissionDate,
      admissionClass: student.admissionClass,
      schoolId: student.schoolId,
      schoolClassId: student.schoolClassId,
      sessionName: student.sessionName,
      sessionId: student.sessionId,
      className: student.className,
      userId: student.userId,
      status: student.status,
      studentPhoto: student.studentPhoto,
      cwsnCode: student.cwsnCode,
      sambalKramank: student.sambalKramank,
    };
  };

  const handleStudentFound = useCallback(async (student: Student) => {
    setIsLoading(true);
    try {
      // Always fetch complete student data using studentCode to ensure we have the correct ID
      if (!student.studentCode) {
        throw new Error('Student code not found');
      }
      
      const studentData = await getStudentByStudentCode(student.studentCode);
      
      if (!studentData.id) {
        throw new Error('Student ID not found in fetched data');
      }
      
      const convertedStudentData = convertStudentToStudentData(studentData);
      setSelectedStudent(convertedStudentData);
      setShowExamForm(true);
    } catch (error) {
      // Only use fallback if the original student data has an ID
      if (student.id) {
        const studentData = convertStudentToStudentData(student);
        setSelectedStudent(studentData);
        setShowExamForm(true);
      } else {
        setError('Unable to load student data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [getStudentByStudentCode]);

  const handleCloseExamForm = () => {
    setShowExamForm(false);
    setSelectedStudent(null);
    setIsLoading(false);
  };

  const handleGetExamForm = () => {
    if (isStudent) {
      // For students, directly use the current student data from useCurrentStudent hook
      if (currentStudentData && currentStudentData.id) {
        handleStudentFound(currentStudentData as Student);
      } else if (currentStudentLoading) {
        // If still loading, show loading state
        setIsLoading(true);
      } else {
        setError('Unable to load your student data. Please try again.');
      }
    } else {
      // For admins/teachers, show the selection modal
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => setModalOpen(false);

  const handleGetExamFormFromDialog = () => {
    setIsLoading(true);
    setTimeout(() => {
      setShowExamForm(true);
      setIsLoading(false);
    }, 1000);
  };

  // Bulk modal controls
  const handleOpenBulkModal = () => {
    // Auto-select the current class and session from context
    if (selectedClass) {
      if ((selectedClass as any)?.section === "__CLASS_ALL__") {
        setBulkSelectedClass("Class-ALL");
      } else if ((selectedClass as any)?.section === "__GRADE_ALL__") {
        setBulkSelectedClass(`${String((selectedClass as any)?.name || "").trim()}-ALL`);
      } else if (selectedClass?.id) {
        setBulkSelectedClass(selectedClass.id);
      }
    }
    if (selectedSession?.id) {
      setBulkSelectedSession(selectedSession.id);
    }
    setBulkModalOpen(true);
    setError('');
    setSuccessMessage('');
  };
  const handleCloseBulkModal = () => {
    if (isGenerating) return;
    setBulkModalOpen(false);
    setBulkSelectedClass('');
    setBulkSelectedSession('');
    setError('');
    setSuccessMessage('');
    setProgress(0);
    setCurrentStudent('');
    setBulkSelectedStudentIds(new Set());
    isBulkDownloadCancelledRef.current = false;
  };
  const handleBulkSessionChange = (e: any) => {
    setBulkSelectedSession(e.target.value);
    // Don't clear class when session changes - preserve class selection
  };
  const handleBulkClassChange = (e: any) => {
    const v = e.target.value;
    if (v === '') return setBulkSelectedClass('');
    const asNumber = Number(v);
    setBulkSelectedClass(Number.isFinite(asNumber) && String(v) === String(asNumber) ? asNumber : String(v));
  };
  const handleCancelBulkDownload = () => {
    isBulkDownloadCancelledRef.current = true;
    setIsGenerating(false);
    setProgress(0);
    setCurrentStudent('');
    showError(t("bulk_download_cancelled") || "Bulk download was cancelled by user");
  };

  /** ---------------- Bulk PDF Generation using individual student rendering ---------------- */
  const handleBulkDownload = async () => {
    setError('');
    setSuccessMessage('');
    setProgress(0);
    setCurrentStudent('');
    isBulkDownloadCancelledRef.current = false;

    if (!bulkSelectedClass || !bulkSelectedSession) {
      setError(t('please_select_both_class_session_bulk_download'));
      return;
    }

    if (bulkVisibleActiveStudents.length === 0) {
      setError(t('no_active_students_found_selected_class_session'));
      return;
    }

    const activeStudents = bulkVisibleActiveStudents.filter((s: any) =>
      bulkSelectedStudentIds.has(s.id)
    );

    if (activeStudents.length === 0) {
      setError(
        (t("please_select_students_to_download") as string) ||
          "Please select at least one student to download."
      );
      return;
    }

    setIsGenerating(true);

    // Create a hidden container for rendering individual students
    let renderContainer: HTMLDivElement | null = null;
    let renderRoot: ReturnType<typeof createRoot> | null = null;

    try {
      const className =
        typeof bulkSelectedClass === "string"
          ? bulkSelectedClass
          : classes.find((c: any) => c.id === bulkSelectedClass)?.name || 'Unknown';
      const sessionName = sessions.find((s: any) => s.id === bulkSelectedSession)?.name || 'Unknown';
      const filename = `bulk-exam-forms-${className}-${sessionName}-${activeStudents.length}-students.pdf`;
      const {
        isNinthClass: bulkIsNinthClass,
        isTenthClass: bulkIsTenthClass,
        isEleventhClass: bulkIsEleventhClass,
        isTwelfthClass: bulkIsTwelfthClass,
      } = detectClassFlags(className);
      if (!bulkIsNinthClass && !bulkIsTenthClass && !bulkIsEleventhClass && !bulkIsTwelfthClass) {
        setError(t('currently_only_10th_class_exam_forms_supported'));
        setIsGenerating(false);
        return;
      }

      // Create hidden container for individual student rendering
      renderContainer = document.createElement('div');
      Object.assign(renderContainer.style, {
        position: 'absolute',
        left: '-10000px',
        top: '0',
        width: '210mm',
        minHeight: '297mm',
        overflow: 'visible',
        backgroundColor: '#ffffff',
        opacity: '0',
        pointerEvents: 'none',
        display: 'block',
      } as CSSStyleDeclaration);
      document.body.appendChild(renderContainer);

      renderRoot = createRoot(renderContainer);

      // Create a combined PDF using jsPDF
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      let pageIndex = 0;

      // Prefetch photo metadata once (replaces N× GET /students/{id}/studentPhoto during bulk)
      const bulkPhotoInfoById = new Map<number, { fileName?: string; signature?: string }>();
      try {
        const idsToFetch = Array.from(
          new Set(
            activeStudents
              .map((s: any) => s?.id)
              .filter((id: any) => typeof id === "number" && !Number.isNaN(id))
          )
        ) as number[];
        if (idsToFetch.length > 0) {
          const photoInfos = await getStudentPhotoInfosBulk(idsToFetch);
          (photoInfos || []).forEach((pi: any) => {
            const sid = Number(pi?.studentId);
            if (!Number.isNaN(sid)) {
              bulkPhotoInfoById.set(sid, {
                fileName: pi?.fileName || "",
                signature: pi?.signature || "",
              });
            }
          });
        }
      } catch {
        // If bulk fails, templates fall back to per-student photo hooks (same as before).
      }

      const buildImageHeaders = (): Record<string, string> => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const tenantSchema = localStorage.getItem("tenantSchema") || sessionStorage.getItem("tenantSchema");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (tenantSchema) headers["X-Tenant-ID"] = tenantSchema;
        return headers;
      };

      const fetchBlobUrlForFileName = async (fileName?: string): Promise<string | null> => {
        if (!fileName || typeof fileName !== "string" || fileName.trim().length === 0) return null;
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/schoolErp";
        const url = `${base}/students/photo/${fileName}`;
        try {
          const res = await fetch(url, { headers: buildImageHeaders() });
          if (!res.ok) return null;
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      };

      // Process each student individually
      for (let idx = 0; idx < activeStudents.length; idx++) {
        // Check for cancellation
        if (isBulkDownloadCancelledRef.current) {
          break;
        }

        const student = activeStudents[idx];
        const studentName = (student as any).studentName || t('unknown_student');
        setCurrentStudent(studentName);

        // Update progress - use functional update to ensure incremental progress
        const pct = Math.round(((idx + 1) / activeStudents.length) * 100);
        setProgress((prevProgress) => Math.max(prevProgress, pct));

        // Yield to browser less frequently for better performance
        if (idx % 5 === 0) {
          await yieldToBrowser();
        }

        // Render individual student (bulk: inject pre-fetched photo URLs; skip per-student studentPhoto API)
        const baseStudentData = convertStudentToStudentData(student);
        const sid = baseStudentData.id;
        const bulkPhotoInfo = typeof sid === "number" ? bulkPhotoInfoById.get(sid) : undefined;
        const skipStudentMediaFetch = typeof sid === "number" && bulkPhotoInfoById.has(sid);

        let photoObjectUrl: string | null = null;
        let signatureObjectUrl: string | null = null;
        try {
          try {
            if (bulkPhotoInfo?.fileName) {
              photoObjectUrl = await fetchBlobUrlForFileName(bulkPhotoInfo.fileName);
            }
            if (bulkPhotoInfo?.signature) {
              signatureObjectUrl = await fetchBlobUrlForFileName(bulkPhotoInfo.signature);
            }
          } catch {
            /* ignore */
          }

          const studentData: StudentData = {
            ...baseStudentData,
            photoUrl: photoObjectUrl,
            signatureUrl: signatureObjectUrl,
            skipStudentMediaFetch,
          };

          const TemplateComponent = bulkIsNinthClass
            ? NinthExamFormTemplate
            : bulkIsTenthClass
            ? TenthExamFromTemplate
            : bulkIsEleventhClass
            ? EleventhExamFormTemplate
            : TwelfthExamFormTemplate;
          renderRoot.render(
            <QueryClientProvider client={bulkQueryClient}>
              <ThemeProvider theme={theme}>
                <AuthProvider>
                  <TemplateComponent studentData={studentData} renderForPDF={true} />
                </AuthProvider>
              </ThemeProvider>
            </QueryClientProvider>
          );

          // Wait for rendering to complete - optimized delays
          await waitNextFrame();
          await sleep(50);

          // Wait for fonts and images
          await waitForFonts();
          await sleep(30);

          // Wait for images to load - reduced timeout
          const images = Array.from(renderContainer.querySelectorAll('img'));
          await Promise.all(
            images.map(img => {
              if (img.complete && (img.naturalWidth || 0) > 0) return Promise.resolve();
              return new Promise<void>(resolve => {
                const timeout = setTimeout(resolve, 1000); // Reduced from 3000ms
                img.onload = img.onerror = () => { clearTimeout(timeout); resolve(); };
              });
            })
          );

          // Check for cancellation after rendering
          if (isBulkDownloadCancelledRef.current) {
            break;
          }

          // Final wait for layout - reduced delay
          await sleep(50);

          // Capture the student's form as image
          const pdfRoot = renderContainer.querySelector('[data-pdf-root]') as HTMLElement;
          if (!pdfRoot) {
            continue;
          }

          const canvas = await html2canvas(pdfRoot, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            background: '#ffffff',
            logging: false,
            scale: 2,
            width: pdfRoot.offsetWidth || pdfRoot.clientWidth,
            height: pdfRoot.offsetHeight || pdfRoot.clientHeight,
            scrollX: 0,
            scrollY: 0,
            windowWidth: pdfRoot.offsetWidth || pdfRoot.clientWidth,
            windowHeight: pdfRoot.offsetHeight || pdfRoot.clientHeight,
            removeContainer: false,
            foreignObjectRendering: false,
            imageTimeout: 0,
          } as any);

          // Check for cancellation after canvas generation
          if (isBulkDownloadCancelledRef.current) {
            break;
          }

          const imgData = canvas.toDataURL("image/jpeg", 0.9);

          // Add pages to PDF
          if (pageIndex > 0) {
            pdf.addPage();
          }

          // Calculate how many pages this student's form needs
          const imgHeight = canvas.height;
          const imgWidth = canvas.width;
          const a4Height = 297; // A4 height in mm
          const a4Width = 210; // A4 width in mm

          // Calculate scale to fit width
          const scale = a4Width / (imgWidth / 2); // Divide by 2 because scale is 2
          const scaledHeight = (imgHeight / 2) * scale; // Divide by 2 because scale is 2
          const pagesNeeded = Math.ceil(scaledHeight / a4Height);

          // Add each page
          for (let page = 0; page < pagesNeeded; page++) {
            if (page > 0) {
              pdf.addPage();
            }

            const yOffset = -page * a4Height;
            pdf.addImage(
              imgData,
              "JPEG",
              0,
              yOffset,
              a4Width,
              scaledHeight,
              undefined,
              'FAST'
            );
            pageIndex++;
          }

          // Yield to browser less frequently
          if (idx % 5 === 0) {
            await yieldToBrowser();
          }
        } finally {
          if (photoObjectUrl) URL.revokeObjectURL(photoObjectUrl);
          if (signatureObjectUrl) URL.revokeObjectURL(signatureObjectUrl);
        }
      }

      // Check if cancelled before saving
      if (isBulkDownloadCancelledRef.current) {
        return;
      }

      // Ensure progress reaches 100% before saving
      setProgress(100);
      await yieldToBrowser();
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Save the combined PDF
      pdf.save(filename);
      const successMsg = t('successfully_generated_bulk_exam_form_pdf', { 
        count: activeStudents.length, 
        className, 
        sessionName 
      });
      // Show success message in top right corner using snackbar
      showSuccess(successMsg);
      
    } catch (error: any) {
      setError(t('failed_generate_bulk_pdf_error', { error: error?.message || 'Unknown error' }));
    } finally {
      // Cleanup
      if (renderRoot) {
        try { renderRoot.unmount(); } catch {}
      }
      if (renderContainer && renderContainer.parentNode) {
        renderContainer.parentNode.removeChild(renderContainer);
      }
      
      setIsGenerating(false);
      setProgress(0);
      setCurrentStudent('');
      isBulkDownloadCancelledRef.current = false;
    }
  };

  if (!selectedClass) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{getFormText('please_select_class_first_view_exam_form', 'please_select_class_first_view_enrollment_form')}</Alert>
      </Box>
    )
  }

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">{getFormText('exam_form_management', 'enrollment_form_management')}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!isStudent && (
              <Button
                variant="contained"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, minWidth: 140 }}
                onClick={handleOpenBulkModal}
                disabled={isGenerating || !isSupportedClass()}
              >
                {getFormText('bulk_download_exam_forms', 'bulk_download_enrollment_forms')}
              </Button>
            )}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#3092e3", 
                minWidth: 140,
                background: isLoading ? 'linear-gradient(135deg, #ccc 0%, #999 100%)' : 'linear-gradient(135deg, #3092e3 0%, #3092e3 100%)',
                boxShadow: isLoading ? 'none' : '0 4px 12px rgba(59, 162, 245, 0.3)',
                '&:hover': {
                  backgroundColor: "primary.main",
                  background: isLoading ? 'linear-gradient(135deg, #ccc 0%, #999 100%)' : 'linear-gradient(135deg, #1976d2 0%, #1976d2 100%)',
                  boxShadow: isLoading ? 'none' : '0 6px 16px rgba(25, 118, 210, 0.4)'
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                  color: '#666'
                }
              }}
              onClick={handleGetExamForm}
              disabled={isLoading || !isSupportedClass()}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? t('loading') : getFormText('get_exam_form', 'get_enrollment_form')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', py: 4 }}>
          {(() => {
            const className = selectedClass?.name || selectedClassName || 'Class';
            const { isNinthClass } = detectClassFlags(className);
            const supported = isSupportedClass();
            
            if (!supported) {
              // Show "No ExamForm/Enrollment form Issued" message for unsupported classes
              return (
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {isNinthClass 
                    ? t('no_enrollment_form_issued_for_class', { className })
                    : t('no_exam_form_issued_for_class', { className })}
                </Typography>
              );
            }
            
            return (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {isNinthClass 
                  ? t('class_enrollment_form_generator', { className }) 
                  : t('class_exam_form_generator', { className })}
              </Typography>
            );
          })()}
          {isSupportedClass() && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {getSubtitleText('generate_official_exam_forms', 'generate_official_enrollment_forms')}
            </Typography>
          )}
        </Box>
      </Paper>

      <GetExamFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onStudentFound={handleStudentFound}
        isLoading={isLoading}
      />

      {/* Exam Form Dialog */}
      <Dialog
        open={showExamForm && selectedStudent !== null}
        onClose={handleCloseExamForm}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: { maxHeight: '90vh', width: '95vw', maxWidth: '1200px' }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#f8f9fb',
          borderBottom: '2px solid #e0e7ff',
          color: '#0168EA',
          fontWeight: 600,
          fontSize: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
<span>{t('exam_form_student_name', { studentName: selectedStudent?.studentName })}</span>
          <IconButton onClick={handleCloseExamForm} sx={{ color: '#0168EA' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {(() => {
            
            // More flexible class name checking - use both selectedClassName and selectedClass.name
            const classNameToCheck = selectedClassName || selectedClass?.name || '';
            const { isNinthClass, isTenthClass, isEleventhClass, isTwelfthClass } = detectClassFlags(classNameToCheck);
            
            if (selectedStudent && isNinthClass) {
              return <NinthExamFormTemplate studentData={selectedStudent} />;
            }
            if (selectedStudent && isTenthClass) {
              return <TenthExamFromTemplate studentData={selectedStudent} />;
            }
            if (selectedStudent && isEleventhClass) {
              return <EleventhExamFormTemplate studentData={selectedStudent} />;
            }
            if (selectedStudent && isTwelfthClass) {
              return <TwelfthExamFormTemplate studentData={selectedStudent} />;
            } else if (selectedStudent && !isTenthClass && !isNinthClass && !isEleventhClass && !isTwelfthClass) {
              return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    {t('exam_form_not_available_class', { className: selectedClass?.name || selectedClassName || 'Class' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('currently_only_10th_class_exam_forms_supported')}
                  </Typography>
                  <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '12px' }}>
                    Debug: Class name = "{classNameToCheck}" (selectedClassName: "{selectedClassName}", selectedClass.name: "{selectedClass?.name}")
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, gap: 2, backgroundColor: '#f8f9fb' }}>
          <Button
            onClick={handleCloseExamForm}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 120,
              borderColor: '#0168EA',
              color: '#0168EA',
              '&:hover': { borderColor: '#0056d3', backgroundColor: 'rgba(1, 104, 234, 0.04)' }
            }}
          >
{t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Download Modal - Only show for non-students */}
      {!isStudent && (
      <Dialog
        open={bulkModalOpen}
        onClose={handleCloseBulkModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {getFormText('bulk_download_exam_forms', 'bulk_download_enrollment_forms')}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ py: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("session")}</InputLabel>
              <Select
                value={bulkSelectedSession}
                onChange={handleBulkSessionChange}
                label={t("session")}
                disabled={isGenerating}
              >
                <MenuItem value="">
                  <em>{t('select_session_placeholder', { defaultValue: 'Select Session' })}</em>
                </MenuItem>
                {sessions.map((session: any) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("class")}</InputLabel>
              <Select
                value={bulkSelectedClass}
                onChange={handleBulkClassChange}
                label={t("class")}
                disabled={isGenerating || !bulkSelectedSession || classesLoading}
              >
                <MenuItem value="">
                  <em>{t('select_class_placeholder', { defaultValue: 'Select Class' })}</em>
                </MenuItem>
                {classesLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">{t('loading_classes')}</Typography>
                    </Box>
                  </MenuItem>
                ) : classesError ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="error">
                      {t('error_loading_classes')}
                    </Typography>
                  </MenuItem>
                ) : classes.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {t('no_classes_found')}
                    </Typography>
                  </MenuItem>
                ) : (
                  (() => {
                    const selectedSessionId = typeof bulkSelectedSession === "string"
                      ? Number(bulkSelectedSession)
                      : bulkSelectedSession;

                    const getClassSortValue = (className?: string) => {
                      if (!className) return Number.MAX_SAFE_INTEGER;
                      const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
                      if (normalized.includes("nursery")) return -3;
                      if (normalized.includes("lkg")) return -2;
                      if (normalized.includes("ukg")) return -1;
                      const m = className.match(/\d+/);
                      return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
                    };
                    const getSectionSortValue = (section?: string | null) => {
                      if (!section) return 0;
                      const s = String(section).trim().toUpperCase();
                      if (!s) return 0;
                      const code = s.charCodeAt(0);
                      return code >= 65 && code <= 90 ? code - 64 : Number.MAX_SAFE_INTEGER;
                    };

                    const base = classes
                      .filter((cls: any) => {
                        if (!selectedSessionId) return true;
                        const classSessionId = cls.sessionId ?? cls.session?.id ?? (cls.sessionId as any)?.id;
                        if (classSessionId !== null && classSessionId !== undefined) {
                          return Number(classSessionId) === Number(selectedSessionId);
                        }
                        return true;
                      })
                      .sort((a: any, b: any) => {
                        const classDiff = getClassSortValue(a?.name) - getClassSortValue(b?.name);
                        if (classDiff !== 0) return classDiff;
                        const sectionDiff = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
                        if (sectionDiff !== 0) return sectionDiff;
                        return (a?.name || "").localeCompare(b?.name || "");
                      });

                    const gradeNames = Array.from(new Set(base.map((c: any) => c?.name).filter(Boolean))) as string[];
                    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

                    const out: any[] = [];
                    out.push(
                      <MenuItem key="__class_all__" value="Class-ALL">
                        Class-ALL
                      </MenuItem>
                    );
                    for (const grade of gradeNames) {
                      out.push(
                        <MenuItem key={`${grade}-ALL`} value={`${grade}-ALL`}>
                          {grade}-ALL
                        </MenuItem>
                      );
                      base
                        .filter((c: any) => c?.name === grade)
                        .forEach((cls: any) => {
                          out.push(
                            <MenuItem key={cls.id} value={cls.id}>
                              {cls.section ? `${cls.name} ${cls.section}` : cls.name}
                            </MenuItem>
                          );
                        });
                    }
                    return out;
                  })()
                )}
              </Select>
            </FormControl>

            {bulkSelectedClass && bulkSelectedSession && (() => {
              const activeCount = bulkVisibleActiveStudents.length;
              const classInfo =
                typeof bulkSelectedClass === "number" ? classes.find((c: any) => c.id === bulkSelectedClass) : null;
              const className =
                typeof bulkSelectedClass === "string"
                  ? bulkSelectedClass
                  : classInfo
                    ? classInfo.section
                      ? `${classInfo.name} ${classInfo.section}`
                      : classInfo.name
                    : "Unknown Class";
              const sessionName = sessions.find((s: any) => s.id === bulkSelectedSession)?.name || "Unknown Session";
              
              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    {t('found_active_students_in_class_session', { 
                      count: activeCount,
                      className: className,
                      sessionName: sessionName
                    })}
                  </Typography>
                </Box>
              );
            })()}

            {bulkVisibleActiveStudents.length > 0 && (
              <TableContainer sx={{ maxHeight: 280, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper" }}>
                        <Checkbox
                          checked={bulkSelectAllChecked}
                          indeterminate={bulkSelectAllIndeterminate}
                          disabled={isGenerating}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkSelectedStudentIds(
                                new Set(
                                  bulkVisibleActiveStudents.map((s: any) => s.id as number)
                                )
                              );
                            } else {
                              setBulkSelectedStudentIds(new Set());
                            }
                          }}
                          inputProps={{
                            "aria-label": (t("select_all") as string) || "Select All",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper" }}>
                        {(t("select_all") as string) || "Select All"} / {t("student_name")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkVisibleActiveStudents.map((student: any) => (
                      <TableRow key={student.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={bulkSelectedStudentIds.has(student.id)}
                            disabled={isGenerating}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setBulkSelectedStudentIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(student.id);
                                else next.delete(student.id);
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>{student.studentName || ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {isGenerating && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <CircularProgress size={20} />
                  {/* <Typography variant="body2">
                    {t("generating_pdf")} {progress}%
                  </Typography> */}
                </Box>
                <LinearProgress variant="determinate" value={progress} />
                {currentStudent && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("processing").replace("{studentName}", currentStudent)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={isGenerating ? handleCancelBulkDownload : handleCloseBulkModal}
            variant="outlined"
          >
            {t("cancel")}
          </Button>

          <LoadingButton
            onClick={handleBulkDownload}
            variant="contained"
            sx={{
              backgroundColor: "#3092e3", 
              "&:hover": { backgroundColor: "primary.main" },
              minWidth: "200px",
              position: "relative",
              "&.Mui-disabled": {
                opacity: isGenerating ? 0.8 : 0.6,
              },
            }}
            loading={isGenerating}
            loadingPosition="start"
            // loadingIndicator={
            //   <Typography variant="body2" fontWeight="medium">
            //     {t("generating_pdf")}
            //   </Typography>
            // }
            disabled={(() => {
              if (!bulkSelectedClass || !bulkSelectedSession) return true;
              if (bulkVisibleActiveStudents.length === 0) return true;
              return bulkSelectedStudentIds.size === 0;
            })()}
          >
            {(() => {
              const activeCount = bulkSelectedStudentIds.size;
              const translationKey = getFormText('download_exam_forms', 'download_enrollment_forms');
              return t(translationKey).replace("{count}", activeCount.toString());
            })()}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      )}
    </>
  )
}

export default ExamFormManagement


